// The file naming window's three commands, wired as the desktop's main process wires them
// (apps/desktop/src/main/main.ts, `starter-filing-plan`, `suggest-filing-plan`,
// `preview-filing-plan`) and answered by the engine host on the job's channel. Paige keeps one
// naming per job, used by every destination that writes files (Job setup > Export, File naming).
import type { FilingPlan } from '../shared/desktop-api';
import type { EngineCall } from './desktop-commands';

/** A type id as the desktop accepts it before it asks the engine. */
const typeIdPattern = /^[a-zA-Z0-9_.:-]{1,120}$/;

const isTypeId = (value: unknown) => typeof value === 'string' && typeIdPattern.test(value);
const isLimit = (value: unknown) =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 50;

/** The desktop's own checks on the preview's focus, refused here before the engine is asked. */
function checkedFocus(focus?: { documentTypeId?: unknown; limit?: unknown }) {
  const { documentTypeId, limit } = focus ?? {};
  if (documentTypeId !== undefined && !isTypeId(documentTypeId))
    throw new Error('The document type identity is invalid.');
  if (limit !== undefined && !isLimit(limit)) throw new Error('The example count is invalid.');
  return {
    ...(documentTypeId !== undefined ? { documentTypeId } : {}),
    ...(limit !== undefined ? { limit } : {}),
  };
}

export function filingCommands(
  call: EngineCall,
): Record<string, (...args: never[]) => Promise<unknown>> {
  return {
    getStarterFilingPlan: (jobId: string) => call('starter-filing-plan', { jobId }),
    // Manager only on the channel; a pool that is down answers Cortexa's reason, shown in the window.
    suggestFilingPlan: (jobId: string) => call('suggest-filing-plan', { jobId }),
    previewFilingPlan: async (
      jobId: string,
      filingPlan: FilingPlan,
      focus?: { documentTypeId?: unknown; limit?: unknown },
    ) => call('preview-filing-plan', { jobId, filingPlan, ...checkedFocus(focus) }),
  };
}
